import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWSRefresh } from '../contexts/WebSocketContext';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Skeleton,
  Alert,
  Tabs,
  Tooltip,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Autocomplete,
  CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Checkbox, FormControlLabel,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ViewKanban as KanbanIcon,
  ViewList as ListIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  Assignment as TicketIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  ShoppingCart as PartsIcon,
  Timeline as TimelineIcon,
  Block as CancelTicketIcon,
  Add as AddItemIcon,
  Delete as DeleteItemIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  Map as MapIcon,
  Folder as FilesTabIcon,
} from '@mui/icons-material';
import { ticketsApi, KanbanTicket, KanbanBoard, customersApi, techniciansApi , partsApi, lineItemsApi, timeTrackingApi, attachmentsApi, ticketOpsApi } from '../services/api';
import TicketContextPanel from '../components/tickets/TicketContextPanel';
import TicketTimeline from '../components/tickets/TicketTimeline';
import TicketBillingPanel from './TicketBillingPanel';
import TicketFilesPanel from './TicketFilesPanel';   // Files tab: Emails / Pictures (tech·office·customer) / Files (2026-09-04)
import TicketFilters, { TicketFilterValue, defaultTicketFilter, ticketMatchesFilter } from '../components/TicketFilters';

const priorityColors: Record<string, { bg: string; color: string }> = {
  urgent: { bg: '#fee2e2', color: '#dc2626' },
  high: { bg: '#ffedd5', color: '#ea580c' },
  medium: { bg: '#fef3c7', color: '#d97706' },
  low: { bg: '#dbeafe', color: '#2563eb' },
};

const statusColors: Record<string, { bg: string; color: string }> = {
  new: { bg: '#e0e7ff', color: '#4f46e5' },
  unassigned: { bg: '#e0e7ff', color: '#4f46e5' },
  assigned: { bg: '#dbeafe', color: '#2563eb' },
  en_route: { bg: '#fef3c7', color: '#d97706' },
  on_site: { bg: '#d1fae5', color: '#059669' },
  in_progress: { bg: '#cffafe', color: '#0891b2' },
  pending_parts: { bg: '#fce7f3', color: '#db2777' },
  awaiting_parts: { bg: '#ede9fe', color: '#6d28d9' },
  pending_review: { bg: '#fef3c7', color: '#b45309' },
  completed: { bg: '#dcfce7', color: '#16a34a' },
  cancelled: { bg: '#f3f4f6', color: '#6b7280' },
};

/* INSTALL jobs (installation tickets, incl. every Installation Order from the bid sheet)
   get their OWN color everywhere - card border, INSTALL chip, list row - so they never
   read as service calls. Service = whatever the column color is. */
const INSTALL_COLOR = '#7c3aed';           // violet
const INSTALL_BG = '#ede9fe';
const isInstall = (t: any) => (t?.ticket_type === 'installation') || !!(t?.custom_fields?.bid_job_id);
const InstallChip = ({ small }: { small?: boolean }) => (
  <Chip label="INSTALL" size="small" sx={{ height: small ? 18 : 20, fontSize: small ? '0.6rem' : '0.65rem', fontWeight: 800, letterSpacing: '.04em', bgcolor: INSTALL_COLOR, color: '#fff' }} />
);

interface KanbanColumnProps {
  title: string;
  tickets: KanbanTicket[];
  color: string;
  onTicketClick: (ticket: KanbanTicket) => void;
  loading?: boolean;
}

function KanbanColumn({ title, tickets, color, onTicketClick, loading }: KanbanColumnProps) {
  return (
    <Box sx={{ minWidth: 300, maxWidth: 300, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 1.5, py: 1.25, borderRadius: '12px 12px 0 0', background: color, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '.04em', fontSize: '0.75rem' }}>{title}</Typography>
        <Chip label={tickets.length} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }} />
      </Box>
      <Box sx={{ flex: 1, p: 1.25, bgcolor: '#EEF1F4', borderRadius: '0 0 12px 12px', border: '1px solid #DFE3E8', borderTop: 'none', overflowY: 'auto', minHeight: 400 }}>
        {loading ? (
          [...Array(3)].map((_, i) => <Skeleton key={i} variant="rectangular" height={120} sx={{ mb: 1.5, borderRadius: 2 }} />)
        ) : tickets.length > 0 ? (
          tickets.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} accent={color} onClick={() => onTicketClick(ticket)} />)
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}><Typography variant="body2">No tickets</Typography></Box>
        )}
      </Box>
    </Box>
  );
}

interface TicketCardProps { ticket: KanbanTicket; onClick: () => void; accent?: string; }

function TicketCard({ ticket, onClick, accent }: TicketCardProps) {
  const priority = priorityColors[ticket.priority] || priorityColors.medium;
  const install = isInstall(ticket);
  const edge = install ? INSTALL_COLOR : (accent || '#0D2233');
  return (
    <Card onClick={onClick} sx={{ mb: 1.25, cursor: 'pointer', transition: 'all 0.15s ease', border: `1px solid ${install ? '#c4b5fd' : '#DFE3E8'}`, borderLeft: `${install ? 6 : 4}px solid ${edge}`, borderRadius: '10px', bgcolor: install ? '#faf8ff' : undefined, '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(13,34,51,0.12)', borderColor: install ? INSTALL_COLOR : '#14548C', borderLeftColor: edge } }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontFamily: '"Source Code Pro", Consolas, monospace', fontSize: '0.7rem' }}>{ticket.ticket_number}</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {install && <InstallChip />}
            <Chip label={ticket.priority.toUpperCase()} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: priority.bg, color: priority.color }} />
          </Box>
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3 }}>{ticket.title}</Typography>
        {ticket.customer_name && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{ticket.customer_name}</Typography>}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled">{ticket.time_since_created || 'Just now'}</Typography>
          </Box>
          {ticket.assigned_to_initials && <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', fontWeight: 700, background: 'linear-gradient(180deg, #1E4A6B, #0D2233)' }}>{ticket.assigned_to_initials}</Avatar>}
        </Box>
      </CardContent>
    </Card>
  );
}

interface TicketRowProps { ticket: KanbanTicket; onClick: () => void; }

function TicketRow({ ticket, onClick }: TicketRowProps) {
  const priority = priorityColors[ticket.priority] || priorityColors.medium;
  const status = statusColors[ticket.status] || statusColors.new;
  const install = isInstall(ticket);
  return (
    <Box onClick={onClick} sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #e2e8f0', borderLeft: install ? `6px solid ${INSTALL_COLOR}` : '6px solid transparent', bgcolor: install ? '#faf8ff' : undefined, cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { bgcolor: install ? INSTALL_BG : '#f8fafc' } }}>
      <Box sx={{ flex: '0 0 140px' }}><Typography variant="body2" sx={{ fontWeight: 600, color: install ? INSTALL_COLOR : '#14548C' }}>{ticket.ticket_number}</Typography>{install && <Box sx={{ mt: 0.25 }}><InstallChip small /></Box>}</Box>
      <Box sx={{ flex: 1 }}><Typography variant="body2" sx={{ fontWeight: 600 }}>{ticket.title}{(ticket as any).return_of_ticket_id ? <Chip label="RETURN" size="small" sx={{ ml: 1, height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#ede9fe', color: '#6d28d9' }} /> : null}</Typography>{ticket.customer_name && <Typography variant="caption" color="text.secondary">{ticket.customer_name}</Typography>}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
          {(ticket as any).missed && <Chip label="MISSED" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#fee2e2', color: '#dc2626' }} />}
          {((ticket as any).needs_return ?? (ticket as any).return_trip_required) && <Chip label="NEEDS RETURN" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#ffedd5', color: '#ea580c' }} />}
          {(ticket as any).aged && <Chip label={`AGED${(ticket as any).aged_days ? ' ' + (ticket as any).aged_days + 'd' : ''}`} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#f3e8ff', color: '#7e22ce' }} />}
        </Box></Box>
      <Box sx={{ flex: '0 0 100px' }}><Chip label={ticket.priority.toUpperCase()} size="small" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 700, bgcolor: priority.bg, color: priority.color }} /></Box>
      <Box sx={{ flex: '0 0 120px' }}><Chip label={ticket.status.replace(/_/g, ' ').toUpperCase()} size="small" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600, bgcolor: status.bg, color: status.color }} /></Box>
      <Box sx={{ flex: '0 0 100px', display: 'flex', alignItems: 'center', gap: 1 }}>{ticket.assigned_to_initials ? (<><Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', fontWeight: 700, background: 'linear-gradient(180deg, #1E4A6B, #0D2233)' }}>{ticket.assigned_to_initials}</Avatar><Typography variant="caption">{ticket.assigned_to_name}</Typography></>) : (<Typography variant="caption" color="text.secondary">Unassigned</Typography>)}</Box>
      <Box sx={{ flex: '0 0 100px' }}><Typography variant="caption" color="text.secondary">{ticket.time_since_created || 'Just now'}</Typography></Box>
    </Box>
  );
}

interface CreateTicketDialogProps { open: boolean; onClose: () => void; onCreated: () => void; }

function CreateTicketDialog({ open, onClose, onCreated }: CreateTicketDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ticket_type: 'service',
    priority: 'medium',
    quickbooks_work_order: '',
    customer_id: null as number | null,
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    service_address_line1: '',
    service_city: '',
    service_state: '',
    service_postal_code: '',
    internal_notes: '',
  });

  useEffect(() => {
    if (open) {
      customersApi.list().then(res => setCustomers(res.data)).catch(console.error);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) { setError('Title is required'); return; }
    setLoading(true); setError(null);
    try {
      await ticketsApi.create(formData);
      onCreated(); onClose();
      setFormData({ title: '', description: '', ticket_type: 'service', priority: 'medium', quickbooks_work_order: '', customer_id: null, contact_name: '', contact_phone: '', contact_email: '', service_address_line1: '', service_city: '', service_state: '', service_postal_code: '', internal_notes: '' });
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to create ticket'); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Create New Ticket</span>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={8}><TextField fullWidth label="Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required /></Grid>
          <Grid item xs={4}><TextField fullWidth label="QuickBooks Work Order #" value={formData.quickbooks_work_order} onChange={(e) => setFormData({ ...formData, quickbooks_work_order: e.target.value })} placeholder="Optional" /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={3} /></Grid>
          <Grid item xs={6}><FormControl fullWidth><InputLabel>Type</InputLabel><Select value={formData.ticket_type} label="Type" onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}><MenuItem value="service">Service Call</MenuItem><MenuItem value="repair">Repair</MenuItem><MenuItem value="installation">Installation</MenuItem><MenuItem value="maintenance">Maintenance</MenuItem><MenuItem value="emergency">Emergency</MenuItem><MenuItem value="inspection">Inspection</MenuItem></Select></FormControl></Grid>
          <Grid item xs={6}><FormControl fullWidth><InputLabel>Priority</InputLabel><Select value={formData.priority} label="Priority" onChange={(e) => setFormData({ ...formData, priority: e.target.value })}><MenuItem value="low">Low</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="urgent">Urgent</MenuItem></Select></FormControl></Grid>
          
          <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Customer & Contact</Typography></Divider></Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={customers}
              getOptionLabel={(option) => option.company || option.name}
              onChange={(_, value) => {
                setFormData({
                  ...formData,
                  customer_id: value?.id || null,
                  contact_name: value?.name || '',
                  contact_phone: value?.phone || '',
                  contact_email: value?.email || '',
                  service_address_line1: value?.address_line1 || '',
                  service_city: value?.city || '',
                  service_state: value?.state || '',
                  service_postal_code: value?.postal_code || '',
                });
              }}
              renderInput={(params) => <TextField {...params} label="Customer" />}
            />
          </Grid>
          <Grid item xs={4}><TextField fullWidth label="Contact Name" value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} /></Grid>
          <Grid item xs={4}><TextField fullWidth label="Contact Phone" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} /></Grid>
          <Grid item xs={4}><TextField fullWidth label="Contact Email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} /></Grid>
          
          <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Service Location</Typography></Divider></Grid>
          <Grid item xs={12}><TextField fullWidth label="Address" value={formData.service_address_line1} onChange={(e) => setFormData({ ...formData, service_address_line1: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start"><LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} /></Grid>
          <Grid item xs={5}><TextField fullWidth label="City" value={formData.service_city} onChange={(e) => setFormData({ ...formData, service_city: e.target.value })} /></Grid>
          <Grid item xs={3}><TextField fullWidth label="State" value={formData.service_state} onChange={(e) => setFormData({ ...formData, service_state: e.target.value })} /></Grid>
          <Grid item xs={4}><TextField fullWidth label="Postal Code" value={formData.service_postal_code} onChange={(e) => setFormData({ ...formData, service_postal_code: e.target.value })} /></Grid>
          
          <Grid item xs={12}><TextField fullWidth label="Internal Notes" value={formData.internal_notes} onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })} multiline rows={2} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: { xs: 1, sm: 2, md: 3 } }}><Button onClick={onClose} disabled={loading}>Cancel</Button><Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />} sx={{ background: 'linear-gradient(180deg, #1E4A6B, #0D2233)', '&:hover': { background: 'linear-gradient(180deg, #123A54, #0A1A28)' } }}>{loading ? 'Creating...' : 'Create Ticket'}</Button></DialogActions>
    </Dialog>
  );
}

interface EditTicketDialogProps { ticket: KanbanTicket | null; open: boolean; onClose: () => void; onSaved: () => void; initialTab?: number; }

function EditTicketDialog({ ticket, open, onClose, onSaved, initialTab }: EditTicketDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => { if (open && initialTab !== undefined) setActiveTab(initialTab); }, [open, initialTab, ticket?.id]);   // deep link ?tab=files (alerts card)
  // D — cancellation (who / why / when) + the per-call pricing-reveal grant
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelCode, setCancelCode] = useState('customer_request');
  const [cancelCustomerRequested, setCancelCustomerRequested] = useState(true); // change 16 (2026-11-01, P2-E): the customer asked -> fee rule applies
  const [cancelResult, setCancelResult] = useState<any>(null);
  const [pricingGrant, setPricingGrant] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [editHistory, setEditHistory] = useState<any[]>([]);
  const [fullTicket, setFullTicket] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});
  const [lineItems, setLineItems] = useState<any[]>([]);
  // v2.3.0 — job photos + customer signature uploaded from the tracker app
  const [attachments, setAttachments] = useState<any[]>([]);
  const [partsSearch, setPartsSearch] = useState('');
  const [partsResults, setPartsResults] = useState<any[]>([]);
  const [newItem, setNewItem] = useState<any>({ description: '', quantity: 1, unit_price: 0, unit_cost: 0, unit_of_measure: 'each', category: 'part', taxable: true, notes: '', labor_units: null });
  const [timeSummary, setTimeSummary] = useState<any>(null);
  const [manualEntry, setManualEntry] = useState({ entry_type: 'onsite', started_at: '', ended_at: '', notes: '' });
  const [addingEntry, setAddingEntry] = useState(false);
  // Portal-action UI state (Phase 7d UI buttons - Agent D)
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [denyOpen, setDenyOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [questionOpen, setQuestionOpen] = useState(false);
  const [questionBody, setQuestionBody] = useState('');
  const [editApproveOpen, setEditApproveOpen] = useState(false);
  // Bid-origin Installation Orders: the approver chooses who gets notified
  const [notifyInhouse, setNotifyInhouse] = useState(true);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  // Mobile-first layout (ticket edit dialog): fullscreen + single-column below sm
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open && ticket) {
      setLoading(true);
      setActiveTab(initialTab ?? 0);   // deep link ?tab=files opens on Files (2026-09-04)
      Promise.all([
        ticketsApi.get(ticket.id),
        customersApi.list(),
        techniciansApi.list(),
        ticketsApi.getHistory(ticket.id).catch(() => ({ data: [] })),
        lineItemsApi.list(ticket.id).catch(() => ({ data: [] }))
      ]).then(([ticketRes, customersRes, techsRes, historyRes, lineItemsRes]) => {
        const t = ticketRes.data;
        setFullTicket(t);
        setFormData({
          title: t.title || '',
          description: t.description || '',
          ticket_type: t.ticket_type || 'service',
          priority: t.priority || 'medium',
          status: t.status || 'new',
          quickbooks_work_order: t.quickbooks_work_order || '',
          customer_id: t.customer_id,
          contact_name: t.contact_name || '',
          contact_phone: t.contact_phone || '',
          contact_email: t.contact_email || '',
          assigned_to_id: t.assigned_to_id,
          service_address_line1: t.service_address_line1 || '',
          service_city: t.service_city || '',
          service_state: t.service_state || '',
          service_postal_code: t.service_postal_code || '',
          scheduled_start: t.scheduled_start || '',
          scheduled_end: t.scheduled_end || '',
          estimated_duration_minutes: t.estimated_duration_minutes || 60,
          internal_notes: t.internal_notes || '',
          customer_notes: t.customer_notes || '',
          resolution_notes: t.resolution_notes || '',
          return_trip_required: t.return_trip_required || false,
          return_reason: t.return_reason || '',
          version: t.version,
        });
        setCustomers(customersRes.data);
        setTechnicians(techsRes.data);
        setEditHistory(historyRes.data);
        setLineItems(lineItemsRes.data || []);
        setPricingGrant(!!(ticketRes.data as any)?.pricing_reveal_granted);
        timeTrackingApi.getTicketSummary(ticket.id).then(ts => setTimeSummary(ts.data)).catch(() => setTimeSummary(null));
        attachmentsApi.list(ticket.id).then(r => setAttachments(r.data.attachments || [])).catch(() => setAttachments([]));
      }).catch(err => {
        setError('Failed to load ticket details');
        console.error(err);
      }).finally(() => setLoading(false));
    }
  }, [open, ticket]);

  const addManualTimeEntry = async () => {
    if (!ticket || !manualEntry.started_at || !manualEntry.ended_at) return;
    setAddingEntry(true);
    try {
      await timeTrackingApi.createEntry({
        ticket_id: ticket.id,
        tech_id: formData.assigned_to_id || 0,
        entry_type: manualEntry.entry_type,
        started_at: manualEntry.started_at,
        ended_at: manualEntry.ended_at,
        notes: manualEntry.notes,
        billable: true,
      });
      const ts = await timeTrackingApi.getTicketSummary(ticket.id);
      setTimeSummary(ts.data);
      setManualEntry({ entry_type: 'onsite', started_at: '', ended_at: '', notes: '' });
    } catch {} finally { setAddingEntry(false); }
  };
  const deleteTimeEntry = async (entryId: number) => {
    if (!ticket) return;
    try {
      await timeTrackingApi.deleteEntry(entryId);
      const ts = await timeTrackingApi.getTicketSummary(ticket.id);
      setTimeSummary(ts.data);
    } catch {}
  };
  const searchParts = async (q: string) => {
    setPartsSearch(q);
    if (q.length >= 2) {
      try { const r = await partsApi.search(q); setPartsResults(r.data); } catch { setPartsResults([]); }
    } else { setPartsResults([]); }
  };
  const addLineItem = async (item?: any) => {
    if (!ticket) return;
    const data = item ? { part_id: item.id, description: item.name, part_number: item.part_number, unit_price: item.unit_price, unit_cost: item.unit_cost, unit_of_measure: item.unit_of_measure, quantity: 1, category: item.category || 'part', taxable: true }
      : { ...newItem };
    if (!data.description) return;
    try {
      const r = await lineItemsApi.add(ticket.id, data);
      setLineItems([...lineItems, r.data]);
      setNewItem({ description: '', quantity: 1, unit_price: 0, unit_cost: 0, unit_of_measure: 'each', category: 'part', taxable: true, notes: '', labor_units: null });
      setPartsSearch(''); setPartsResults([]);
    } catch {}
  };
  const removeLineItem = async (itemId: number) => {
    if (!ticket) return;
    try { await lineItemsApi.delete(ticket.id, itemId); setLineItems(lineItems.filter(li => li.id !== itemId)); } catch {}
  };
  const updateLineItem = async (itemId: number, field: string, value: any) => {
    if (!ticket) return;
    try {
      const r = await lineItemsApi.update(ticket.id, itemId, { [field]: value });
      setLineItems(lineItems.map(li => li.id === itemId ? r.data : li));
    } catch {}
  };
  const lineTotal = lineItems.reduce((sum, li) => sum + (li.line_total || 0), 0);
  const lineCost = lineItems.reduce((sum, li) => sum + (li.line_cost || 0), 0);
  const taxableTotal = lineItems.filter(li => li.taxable).reduce((sum, li) => sum + (li.line_total || 0), 0);
  const tax = taxableTotal * 0.0875;
  const grandTotal = lineTotal + tax;

  // ---- Portal actions: approve / deny / question / edit / edit+approve ----
  const refreshTicketAfter = async () => {
    if (!ticket) return;
    try {
      const fresh = await ticketsApi.get(ticket.id);
      setFullTicket(fresh.data);
      setFormData((p: any) => ({ ...p, status: fresh.data.status, version: fresh.data.version }));
    } catch {}
    onSaved();
  };
  const doPortalAction = async (
    action: 'approve' | 'deny' | 'question' | 'edit',
    opts: { changes?: any; comment?: string; silent?: boolean } = {},
  ) => {
    if (!ticket) return;
    setActionBusy(action); setActionError(null);
    try {
      const bidExtras = (fullTicket as any)?.custom_fields?.bid_job_id
        ? { notify_inhouse: notifyInhouse, notify_customer: notifyCustomer } : {};
      await ticketsApi.portalAction(ticket.id, { action, ...opts, ...bidExtras });
      await refreshTicketAfter();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || `${action} failed`;
      setActionError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      throw err;
    } finally { setActionBusy(null); }
  };
  // Bid-origin: manager sign-off on the overage amount charged to the invoice
  const handleOverageSignoff = async () => {
    const amt = window.prompt('MANAGER SIGN-OFF - overage amount to charge on the invoice ($):', '0');
    if (amt === null) return;
    const note = window.prompt('Note (change-order reference / reason):', '') || '';
    try {
      await ticketsApi.overageSignoff(ticket!.id, { amount: parseFloat(amt) || 0, note });
      await refreshTicketAfter();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'sign-off failed';
      setActionError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };
  const handleApprove = () => doPortalAction('approve').catch(() => {});
  const handleSubmitDeny = async () => {
    if (!denyReason.trim()) return;
    try {
      await doPortalAction('deny', { comment: denyReason.trim() });
      setDenyOpen(false); setDenyReason('');
    } catch {}
  };
  const handleSubmitQuestion = async () => {
    if (!questionBody.trim()) return;
    try {
      await doPortalAction('question', { comment: questionBody.trim() });
      setQuestionOpen(false); setQuestionBody('');
    } catch {}
  };
  const buildEditChanges = () => {
    if (!fullTicket) return {};
    const candidate: any = {
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      ticket_type: formData.ticket_type,
      contact_name: formData.contact_name,
      contact_phone: formData.contact_phone,
      contact_email: formData.contact_email,
      internal_notes: formData.internal_notes,
      customer_notes: formData.customer_notes,
      service_address_line1: formData.service_address_line1,
      service_city: formData.service_city,
      service_state: formData.service_state,
      service_postal_code: formData.service_postal_code,
    };
    const changes: any = {};
    Object.entries(candidate).forEach(([k, v]) => {
      if (v !== fullTicket[k] && v !== undefined) changes[k] = v;
    });
    return changes;
  };
  const handleEdit = async () => {
    const changes = buildEditChanges();
    if (Object.keys(changes).length === 0) {
      setActionError('No changes to save.');
      return;
    }
    try { await doPortalAction('edit', { changes }); } catch {}
  };
  const handleEditAndApprove = async () => {
    const changes = buildEditChanges();
    try {
      if (Object.keys(changes).length > 0) {
        await doPortalAction('edit', { changes });
      }
      await doPortalAction('approve');
    } catch {}
  };

  const handleSave = async () => {
    if (!ticket) return;
    setSaving(true); setError(null);
    try {
      await ticketsApi.update(ticket.id, formData);
      onSaved();
      onClose();
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError('This ticket has been modified by another user. Please refresh and try again.');
      } else {
        setError(err.response?.data?.detail || 'Failed to save ticket');
      }
    } finally { setSaving(false); }
  };

  if (!ticket) return null;

  const priority = priorityColors[ticket.priority] || priorityColors.medium;
  const status = statusColors[ticket.status] || statusColors.new;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile} PaperProps={{ sx: { minHeight: isMobile ? '100%' : '80vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', px: { xs: 1.5, sm: 3 }, py: { xs: 1, sm: 2 }, flexShrink: 0, gap: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{ticket.ticket_number}</Typography>
            <Chip label={ticket.priority.toUpperCase()} size="small" sx={{ bgcolor: priority.bg, color: priority.color, fontWeight: 700 }} />
            <Chip label={ticket.status.replace(/_/g, ' ').toUpperCase()} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 600 }} />
            {(fullTicket as any)?.return_trip_required && <Chip label="⚠ RETURN TRIP REQUIRED" size="small" sx={{ bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 700, border: '1px solid #fdba74' }} />}
          </Box>
          {fullTicket?.quickbooks_work_order && (
            <Typography variant="caption" color="text.secondary">QB Work Order: {fullTicket.quickbooks_work_order}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      
      <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant={isMobile ? 'scrollable' : 'standard'} scrollButtons={isMobile ? 'auto' : false} allowScrollButtonsMobile sx={{ px: { xs: 0.5, sm: 3 }, minHeight: { xs: 40, sm: 48 } }}>
          <Tab icon={<EditIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Edit Details" />
          <Tab icon={<PartsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Parts & Pricing" />
          <Tab icon={<TimeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Time Tracking" />
          <Tab icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Edit History" />
          <Tab icon={<TimelineIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Timeline" />
          <Tab icon={<FilesTabIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Files" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, flex: '1 1 auto', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>
        ) : activeTab === 0 ? (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
<Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#14548C' }}>Basic Information</Typography></AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}><TextField fullWidth label="Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required multiline={isMobile} minRows={isMobile ? 2 : undefined} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Ticket #" value={ticket?.ticket_number || ''} InputProps={{ readOnly: true }} /></Grid>
                  <Grid item xs={12}><TextField fullWidth label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={3} /></Grid>
                  <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Type</InputLabel><Select value={formData.ticket_type} label="Type" onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}><MenuItem value="service">Service Call</MenuItem><MenuItem value="repair">Repair</MenuItem><MenuItem value="installation">Installation</MenuItem><MenuItem value="maintenance">Maintenance</MenuItem><MenuItem value="emergency">Emergency</MenuItem><MenuItem value="inspection">Inspection</MenuItem></Select></FormControl></Grid>
                  <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Priority</InputLabel><Select value={formData.priority} label="Priority" onChange={(e) => setFormData({ ...formData, priority: e.target.value })}><MenuItem value="low">Low</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="urgent">Urgent</MenuItem></Select></FormControl></Grid>
                  <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Status</InputLabel><Select value={formData.status} label="Status" onChange={(e) => setFormData({ ...formData, status: e.target.value })}><MenuItem value="awaiting_parts">Awaiting Parts (install)</MenuItem><MenuItem value="pending_review">Pending Review</MenuItem><MenuItem value="needs_info">Needs Info</MenuItem><MenuItem value="denied">Denied</MenuItem><MenuItem value="new">New</MenuItem><MenuItem value="unassigned">Unassigned</MenuItem><MenuItem value="assigned">Assigned</MenuItem><MenuItem value="en_route">En Route</MenuItem><MenuItem value="on_site">On Site</MenuItem><MenuItem value="in_progress">In Progress</MenuItem><MenuItem value="pending_parts">Pending Parts</MenuItem><MenuItem value="completed">Completed</MenuItem><MenuItem value="cancelled">Cancelled</MenuItem></Select></FormControl></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            {/* v2.3.3 — travel start point, ACTUAL vs BILLED time, leg mileage, customer copy */}
            <Accordion disableGutters sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#14548C' }}>Travel &amp; Billing (actual vs billed)</Typography></AccordionSummary>
              <AccordionDetails>
                {ticket?.id ? <TicketBillingPanel ticketId={ticket.id} /> : null}
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#14548C' }}>Customer &amp; Assignment</Typography></AccordionSummary>
              <AccordionDetails>
                {/* Franchise / corporation / owner + every contact linked to this site,
                    and the ENTIRE service address — not just the location name. */}
                {ticket?.id ? <TicketContextPanel ticketId={ticket.id} /> : null}
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><Autocomplete options={customers} value={customers.find(c => c.id === formData.customer_id) || null} getOptionLabel={(option) => option.company || option.name} onChange={(_, value) => setFormData({ ...formData, customer_id: value?.id || null })} renderInput={(params) => <TextField {...params} label="Customer" />} /></Grid>
                  <Grid item xs={12} sm={6}><Autocomplete options={technicians} value={technicians.find(t => t.id === formData.assigned_to_id) || null} getOptionLabel={(option) => (`${option.first_name || ''} ${option.last_name || ''}`).trim() || option.email} onChange={(_, value) => setFormData({ ...formData, assigned_to_id: value?.id || null })} renderInput={(params) => <TextField {...params} label="Assigned Technician" />} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Contact Name" value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Contact Phone" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Contact Email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} /></Grid>
                  <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Schedule</Typography></Divider></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth type="datetime-local" label="Scheduled start" InputLabelProps={{ shrink: true }} value={(formData.scheduled_start || '').slice(0,16)} onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth type="datetime-local" label="Scheduled end" InputLabelProps={{ shrink: true }} value={(formData.scheduled_end || '').slice(0,16)} onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Expected service hours" inputProps={{ step: 0.5, min: 0 }} value={formData.estimated_duration_minutes ? (formData.estimated_duration_minutes/60) : ''} onChange={(e) => setFormData({ ...formData, estimated_duration_minutes: Math.round((parseFloat(e.target.value)||0)*60) })} /></Grid>
                  <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">Return Visit</Typography></Divider></Grid>
                  <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={!!formData.return_trip_required} onChange={(e) => setFormData({ ...formData, return_trip_required: e.target.checked })} />
                    <Typography variant="body2">Incomplete - needs return visit</Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}><TextField fullWidth label="Return reason" value={formData.return_reason || ''} onChange={(e) => setFormData({ ...formData, return_reason: e.target.value })} placeholder="What still needs to be done" /></Grid>
                  {fullTicket?.return_of_ticket_id && (
                    <Grid item xs={12}><Alert severity="info" sx={{ py: 0 }}>This is a return visit for ticket #{fullTicket.return_of_ticket_id}. Prior time/parts stay billable on the original.</Alert></Grid>
                  )}
                  {fullTicket?.return_ticket_id && (
                    <Grid item xs={12}><Alert severity="success" sx={{ py: 0 }}>A linked return call (#{fullTicket.return_ticket_id}) was created from this ticket.</Alert></Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#14548C' }}>Service Location</Typography></AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}><TextField fullWidth label="Site / Location name (Concept-managed)" value={(ticket?.title || '').split(' @ ').slice(1).join(' @ ')} InputProps={{ readOnly: true }} helperText="Name & national store number are managed by Concept; customers may update address only." /></Grid>
                  <Grid item xs={12}><TextField fullWidth label="Address" value={formData.service_address_line1} onChange={(e) => setFormData({ ...formData, service_address_line1: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={5}><TextField fullWidth label="City" value={formData.service_city} onChange={(e) => setFormData({ ...formData, service_city: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={3}><TextField fullWidth label="State" value={formData.service_state} onChange={(e) => setFormData({ ...formData, service_state: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Postal Code" value={formData.service_postal_code} onChange={(e) => setFormData({ ...formData, service_postal_code: e.target.value })} /></Grid>
                  <Grid item xs={12}><Link href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([formData.service_address_line1, formData.service_city, formData.service_state, formData.service_postal_code].filter(Boolean).join(', '))}`} target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}><MapIcon fontSize="small" /> Open location in Maps</Link></Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion disableGutters sx={{ mb: 1, borderLeft: '4px solid #d32f2f' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#000' }}>Internal Notes</Typography></AccordionSummary>
              <AccordionDetails><TextField fullWidth value={formData.internal_notes} onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })} multiline minRows={3} /></AccordionDetails>
            </Accordion>
            <Accordion disableGutters sx={{ mb: 1, borderLeft: '4px solid #2e7d32' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#000' }}>Customer Notes</Typography></AccordionSummary>
              <AccordionDetails><TextField fullWidth value={formData.customer_notes} onChange={(e) => setFormData({ ...formData, customer_notes: e.target.value })} multiline minRows={3} /></AccordionDetails>
            </Accordion>
            <Accordion disableGutters sx={{ mb: 1, borderLeft: '4px solid #1976d2' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#000' }}>Resolution Notes</Typography></AccordionSummary>
              <AccordionDetails><TextField fullWidth value={formData.resolution_notes} onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })} multiline minRows={3} /></AccordionDetails>
            </Accordion>
          </Box>
        ) : activeTab === 1 ? (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {/* Parts Search */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#14548C' }}>Search Parts Catalog</Typography>
            <TextField fullWidth size="small" placeholder="Search by name, part number, or description..." value={partsSearch}
              onChange={e => searchParts(e.target.value)} sx={{ mb: 1 }} />
            {partsResults.length > 0 && (
              <Box sx={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 1, mb: 2 }}>
                {partsResults.map(p => (
                  <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                    onClick={() => addLineItem(p)}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{p.part_number}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.name} - {p.manufacturer}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={700} color="primary">${p.unit_price?.toFixed(2)}/{p.unit_of_measure}</Typography>
                      <Typography variant="caption" color="text.secondary">Cost: ${p.unit_cost?.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Line Items Table */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, mt: 2, color: '#14548C' }}>Line Items</Typography>
            <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Part #</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>U/M</TableCell>
                    <Tooltip title="Labor units for this item. One unit = one hour."><TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Units</TableCell></Tooltip>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Price</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Cond</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Disc%</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Total</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="center">Tax</TableCell>
                    <TableCell sx={{ width: 40 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map(li => (
                    <TableRow key={li.id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                      <TableCell sx={{ fontSize: 12 }}>{li.part_number || '-'}</TableCell>
                      <TableCell>
                        <TextField variant="standard" size="small" value={li.description} fullWidth
                          onChange={e => updateLineItem(li.id, 'description', e.target.value)}
                          InputProps={{ sx: { fontSize: 12 } }} />
                      </TableCell>
                      <TableCell align="center" sx={{ width: 70 }}>
                        <TextField variant="standard" size="small" type="number" value={li.quantity}
                          onChange={e => updateLineItem(li.id, 'quantity', parseFloat(e.target.value) || 0)}
                          InputProps={{ sx: { fontSize: 12, textAlign: 'center' } }} sx={{ width: 50 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{li.unit_of_measure}</TableCell>
                      <TableCell align="center" sx={{ width: 68 }}>
                        <TextField variant="standard" size="small" type="number"
                          value={li.labor_units ?? li.part_labor_units ?? ''}
                          placeholder="—"
                          onChange={e => updateLineItem(li.id, 'labor_units', e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
                          inputProps={{ step: 0.25, min: 0 }}
                          InputProps={{ sx: { fontSize: 12, textAlign: 'center' } }} sx={{ width: 52 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 90 }}>
                        <TextField variant="standard" size="small" type="number" value={li.unit_price}
                          onChange={e => updateLineItem(li.id, 'unit_price', parseFloat(e.target.value) || 0)}
                          InputProps={{ sx: { fontSize: 12, textAlign: 'right' }, startAdornment: <span style={{ fontSize: 11, color: '#999' }}>$</span> }}
                          sx={{ width: 80 }} />
                      </TableCell>
                      <TableCell sx={{ width: 92 }}>
                        <Select variant="standard" size="small" value={li.item_condition || 'new'}
                          onChange={e => updateLineItem(li.id, 'item_condition', e.target.value)}
                          sx={{ fontSize: 11 }}>
                          <MenuItem value="new">New</MenuItem>
                          <MenuItem value="refurbished">Refurb</MenuItem>
                          <MenuItem value="used">Used</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell align="right" sx={{ width: 64 }}>
                        <TextField variant="standard" size="small" type="number" value={li.discount_percent || 0}
                          onChange={e => updateLineItem(li.id, 'discount_percent', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                          InputProps={{ sx: { fontSize: 12, textAlign: 'right' } }} sx={{ width: 48 }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: (li.discount_percent || 0) > 0 ? '#d97706' : 'inherit' }}>${li.line_total?.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Checkbox size="small" checked={li.taxable} onChange={e => updateLineItem(li.id, 'taxable', e.target.checked)} sx={{ p: 0 }} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeLineItem(li.id)} color="error" sx={{ p: 0.25 }}>
                          <DeleteItemIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {lineItems.length === 0 && (
                    <TableRow><TableCell colSpan={11} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No line items. Search the parts catalog above or add manually below.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Manual add row */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mb: 2, flexWrap: 'wrap' }}>
              <TextField size="small" label="Description" value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} sx={{ flex: 2, minWidth: 200 }} />
              <TextField size="small" label="Qty" type="number" value={newItem.quantity} onChange={e => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 1})} sx={{ width: 70 }} />
              <Tooltip title="One unit = one hour">
                <TextField size="small" label="Units" type="number" value={(newItem as any).labor_units ?? ''}
                  onChange={e => setNewItem({ ...newItem, labor_units: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) } as any)}
                  inputProps={{ step: 0.25, min: 0 }} sx={{ width: 80 }} />
              </Tooltip>
              <TextField size="small" label="Price" type="number" value={newItem.unit_price} onChange={e => setNewItem({...newItem, unit_price: parseFloat(e.target.value) || 0})} sx={{ width: 90 }}
                InputProps={{ startAdornment: <span style={{ fontSize: 11, color: '#999', marginRight: 2 }}>$</span> }} />
              <Button variant="outlined" size="small" startIcon={<AddItemIcon />} onClick={() => addLineItem()} disabled={!newItem.description}>Add</Button>
            </Box>

            {/* Totals */}
            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 2, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={600}>${lineTotal.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Tax (8.75%):</Typography>
                <Typography variant="body2">${tax.toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight={700}>Total:</Typography>
                <Typography variant="subtitle1" fontWeight={700} color="primary">${grandTotal.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Labor units (1 unit = 1 hour):</Typography>
                <Typography variant="caption" fontWeight={700}>
                  {lineItems.reduce((sum, li) => sum + ((li.quantity || 0) * ((li.labor_units ?? li.part_labor_units) || 0)), 0).toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Margin:</Typography>
                <Typography variant="caption" color="success.main">${(lineTotal - lineCost).toFixed(2)} ({lineTotal > 0 ? ((1 - lineCost/lineTotal) * 100).toFixed(0) : 0}%)</Typography>
              </Box>
            </Box>
          </Box>
        ) : activeTab === 5 ? (
          ticket?.id ? <TicketFilesPanel ticketId={ticket.id} defaultSource="office" /> : null
        ) : activeTab === 4 ? (
          ticket?.id ? <TicketTimeline ticketId={ticket.id} /> : null
        ) : activeTab === 2 ? (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#14548C' }}>Time Tracking</Typography>
            {timeSummary ? (
              <>
                {/* Summary cards */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 120, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">On-Site</Typography>
                    <Typography variant="h5" fontWeight={700} color="success.dark">
                      {timeSummary.total_onsite_minutes >= 60
                        ? `${Math.floor(timeSummary.total_onsite_minutes/60)}h ${Math.round(timeSummary.total_onsite_minutes%60)}m`
                        : `${Math.round(timeSummary.total_onsite_minutes)}m`}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 120, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Travel</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.dark">
                      {timeSummary.total_travel_minutes >= 60
                        ? `${Math.floor(timeSummary.total_travel_minutes/60)}h ${Math.round(timeSummary.total_travel_minutes%60)}m`
                        : `${Math.round(timeSummary.total_travel_minutes)}m`}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 120, p: 2, bgcolor: '#fff3e0', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Suspended</Typography>
                    <Typography variant="h5" fontWeight={700} color="warning.dark">
                      {timeSummary.total_suspended_minutes >= 60
                        ? `${Math.floor(timeSummary.total_suspended_minutes/60)}h ${Math.round(timeSummary.total_suspended_minutes%60)}m`
                        : `${Math.round(timeSummary.total_suspended_minutes)}m`}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 120, p: 2, bgcolor: '#f3e5f5', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Visits</Typography>
                    <Typography variant="h5" fontWeight={700} color="secondary.dark">{timeSummary.visit_count}</Typography>
                  </Box>
                </Box>

                {/* Time entries table */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#14548C' }}>Time Log</Typography>
                {timeSummary.entries?.length > 0 ? (
                  <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Visit</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Started</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Ended</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Duration</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Miles</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Source</TableCell>
                          <TableCell sx={{ width: 36 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timeSummary.entries.map((e: any) => (
                          <TableRow key={e.id} sx={{ bgcolor: !e.ended_at ? 'rgba(25,118,210,0.05)' : 'transparent' }}>
                            <TableCell>
                              <Chip label={e.entry_type} size="small" sx={{
                                height: 20, fontSize: 10, fontWeight: 700,
                                bgcolor: e.entry_type === 'onsite' ? '#e8f5e9' : e.entry_type === 'travel' ? '#e3f2fd' : '#fff3e0',
                                color: e.entry_type === 'onsite' ? '#2e7d32' : e.entry_type === 'travel' ? '#1565c0' : '#e65100',
                              }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{e.entry_type === 'onsite' ? `#${e.visit_number}` : '-'}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{e.started_at ? new Date(e.started_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                            <TableCell sx={{ fontSize: 11 }}>{e.ended_at ? new Date(e.ended_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : <Chip label="LIVE" size="small" color="primary" sx={{ height: 16, fontSize: 9 }} />}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>
                              {e.duration_minutes >= 60 ? `${Math.floor(e.duration_minutes/60)}h ${Math.round(e.duration_minutes%60)}m` : `${e.duration_minutes.toFixed(1)}m`}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 12 }}>{e.distance_miles > 0 ? `${e.distance_miles.toFixed(1)}` : '-'}</TableCell>
                            <TableCell sx={{ fontSize: 10 }}>
                              {e.auto_generated ? 'GPS Auto' : (e.created_by_name || 'Manual')}
                              {e.adjusted && <Chip label="ADJUSTED" size="small" title={e.adjustment_note || 'flagged for billing review'} sx={{ ml: 0.5, height: 16, fontSize: 8, fontWeight: 700, bgcolor: '#fef3c7', color: '#b45309' }} />}
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" title="Adjust duration (flagged for billing review)" sx={{ p: 0.25 }} onClick={async () => {
                                const v = window.prompt('New duration in minutes (this adjustment is flagged for billing review):', String(Math.round(e.duration_minutes)));
                                if (v === null) return; const mins = parseFloat(v); if (isNaN(mins) || mins < 0) return;
                                const reason = window.prompt('Reason for adjustment (optional):') || undefined;
                                try { await timeTrackingApi.updateEntry(e.id, { duration_minutes: mins, reason });
                                  const ts = await timeTrackingApi.getTicketSummary(ticket.id); setTimeSummary(ts.data); } catch {}
                              }}>
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                              <IconButton size="small" onClick={() => deleteTimeEntry(e.id)} sx={{ p: 0.25 }} color="error">
                                <DeleteItemIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" variant="body2">No time entries recorded yet. Time tracking runs automatically via GPS proximity.</Typography>
                )}

                {/* Manual time entry form */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#14548C' }}>Add Manual Entry</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={manualEntry.entry_type} label="Type"
                      onChange={e => setManualEntry({...manualEntry, entry_type: e.target.value})}>
                      <MenuItem value="onsite">On-Site</MenuItem>
                      <MenuItem value="travel">Travel / Drive Time</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField size="small" label="Start" type="datetime-local" value={manualEntry.started_at}
                    onChange={e => setManualEntry({...manualEntry, started_at: e.target.value})}
                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />
                  <TextField size="small" label="End" type="datetime-local" value={manualEntry.ended_at}
                    onChange={e => setManualEntry({...manualEntry, ended_at: e.target.value})}
                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />
                  <TextField size="small" label="Notes" value={manualEntry.notes}
                    onChange={e => setManualEntry({...manualEntry, notes: e.target.value})}
                    sx={{ flex: 1, minWidth: 150 }} />
                  <Button variant="contained" size="small" onClick={addManualTimeEntry}
                    disabled={addingEntry || !manualEntry.started_at || !manualEntry.ended_at}
                    sx={{ background: 'linear-gradient(180deg, #1E4A6B, #0D2233)', height: 40 }}>
                    {addingEntry ? 'Adding...' : 'Add Entry'}
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Manual entries are logged with your name and timestamp for audit.
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary" variant="body2">Loading time data...</Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#14548C' }}>Edit History</Typography>
            {editHistory.length === 0 ? (
              <Typography color="text.secondary">No edit history available</Typography>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {editHistory.map((edit, index) => (
                  <Box key={edit.id || index} sx={{ p: 2, mb: 1, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{edit.field_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(edit.created_at).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">From:</Typography>
                        <Typography variant="body2">{edit.old_value || '(empty)'}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">To:</Typography>
                        <Typography variant="body2">{edit.new_value || '(empty)'}</Typography>
                      </Box>
                    </Box>
                    {edit.changed_by && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Changed by: {edit.changed_by.first_name} {edit.changed_by.last_name} ({edit.changed_by.email})
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        {/* ===== v2.3.0 ATTACHMENTS: job photos + customer signature ===== */}
        <Box sx={{ px: 3, pb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Pictures ({attachments.filter((a: any) => a.kind !== 'file').length})
            <Button size="small" onClick={() => setActiveTab(5)} sx={{ ml: 1, textTransform: 'none' }}>See the Files tab for documents, emails and all pictures →</Button>
          </Typography>
          {attachments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No photos or signature uploaded from the field yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {attachments.filter((a: any) => a.kind !== 'file').map((a: any) => (
                <Box key={a.id} sx={{ width: 160, border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
                  <a href={(a.url || attachmentsApi.fileUrl(a.id))} target="_blank" rel="noreferrer">
                    <Box
                      component="img"
                      src={(a.url || attachmentsApi.fileUrl(a.id))}
                      alt={a.original_filename}
                      sx={{ width: '100%', height: 110, objectFit: 'contain', bgcolor: '#f8fafc', display: 'block' }}
                    />
                  </a>
                  <Box sx={{ p: 1 }}>
                    <Chip
                      size="small"
                      label={a.kind === 'signature' ? 'SIGNATURE' : a.kind === 'photo' ? 'PHOTO' : 'FILE'}
                      color={a.kind === 'signature' ? 'secondary' : 'primary'}
                      sx={{ mb: 0.5 }}
                    />
                    <Typography variant="caption" display="block" noWrap title={a.original_filename}>
                      {a.original_filename}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {a.uploaded_by?.name || ''}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

      </DialogContent>

      {/* Portal-action errors (Agent D) */}
      {actionError && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      <DialogActions sx={{ p: { xs: 1, sm: 3 }, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1, position: 'sticky', bottom: 0, zIndex: 2, flexShrink: 0, bgcolor: 'background.paper', maxHeight: { xs: '28vh', sm: 'none' }, overflowY: { xs: 'auto', sm: 'visible' }, '& .MuiButton-root': { minHeight: 36, ...(isMobile ? { flex: '1 1 auto', fontSize: 12, px: 1, py: 0.5 } : {}) } }}>
        {(fullTicket as any)?.custom_fields?.overage && (
          <Box sx={{ width: '100%', p: 1, mb: 1, bgcolor: '#fff4e5', border: '1px solid #ffb74d', borderRadius: 1, fontSize: 13 }}>
            <b>OVERAGE - beyond the sold bid budget.</b>{' '}
            {(((fullTicket as any).custom_fields.overage || {}).details || []).join('; ')}
            {(fullTicket as any)?.custom_fields?.overage_signoff ? (
              <> {' - '}<b>signed off</b>: ${String((fullTicket as any).custom_fields.overage_signoff.amount)} by {(fullTicket as any).custom_fields.overage_signoff.by}</>
            ) : (
              <>{' - '}request a CHANGE ORDER from the customer; a manager must sign off before this job can be invoiced.
                <Button size="small" variant="outlined" color="warning" sx={{ ml: 1 }} onClick={handleOverageSignoff}>Manager sign-off</Button></>
            )}
          </Box>
        )}
        <Button onClick={onClose} disabled={saving || !!actionBusy}>Close</Button>
        <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />
        {/* AWAITING_PARTS: install order from the bid sheet - parts are being classified /
            received. "Parts cleared" (or the bid sheet's "Parts received") moves it into the
            ticket process (pending review). */}
        {(fullTicket?.status === 'awaiting_parts') && (
          <>
            <Box sx={{ width: '100%', p: 1, mb: 1, bgcolor: INSTALL_BG, border: `1px solid ${INSTALL_COLOR}`, borderRadius: 1, fontSize: 13, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <InstallChip />
              <b>Awaiting parts classification</b>
              {(fullTicket as any)?.custom_fields?.bid_job_id && <span>- Installation Order from bid <b>{(fullTicket as any).custom_fields.bid_job_id}</b>{(fullTicket as any).custom_fields.sales_order ? <> - Sales Order {(fullTicket as any).custom_fields.sales_order}</> : null}</span>}
              <span style={{ opacity: .75 }}>- clears automatically when every line is marked received on the bid sheet, or clear it here.</span>
            </Box>
            <Button
              variant="outlined"
              color="error"
              disabled={!!actionBusy}
              startIcon={<CancelTicketIcon sx={{ fontSize: 16 }} />}
              onClick={() => { setCancelReason(''); setCancelResult(null); setCancelOpen(true); }}
            >
              Cancel Ticket
            </Button>
            <Button
              variant="outlined"
              disabled={!!actionBusy || activeTab !== 0}
              onClick={handleEdit}
              startIcon={actionBusy === 'edit' ? <CircularProgress size={16} /> : <EditIcon />}
            >
              {actionBusy === 'edit' ? 'Saving...' : 'Save Edits'}
            </Button>
            <Button
              variant="contained"
              disabled={!!actionBusy}
              sx={{ bgcolor: INSTALL_COLOR, '&:hover': { bgcolor: '#6d28d9' } }}
              onClick={async () => {
                const note = window.prompt('Parts cleared - the install order moves to PENDING REVIEW (the normal ticket process). Note (optional):', '');
                if (note === null) return;
                setActionBusy('parts');
                try { await ticketsApi.partsCleared(ticket.id, note || ''); await refreshTicketAfter(); }
                catch (err: any) { setActionError(err?.response?.data?.detail || 'Parts cleared failed'); }
                finally { setActionBusy(null); }
              }}
            >
              {actionBusy === 'parts' ? 'Working...' : 'Parts cleared -> Pending Review'}
            </Button>
          </>
        )}
        {/* PENDING_REVIEW: approve / deny / question / edit / edit+approve */}
        {(fullTicket?.status === 'pending_review') && (
          <>
            {(fullTicket as any)?.custom_fields?.bid_job_id && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mr: 1 }}>
                <Chip size="small" color="info" label={`Installation Order - Bid ${(fullTicket as any).custom_fields.bid_job_id}`} />
                <FormControlLabel sx={{ mr: 0 }} label="Notify in-house"
                  control={<Checkbox size="small" checked={notifyInhouse} onChange={e => setNotifyInhouse(e.target.checked)} />} />
                <FormControlLabel sx={{ mr: 0 }} label="Notify customer"
                  control={<Checkbox size="small" checked={notifyCustomer} onChange={e => setNotifyCustomer(e.target.checked)} />} />
              </Box>
            )}
            <Button
              variant="outlined"
              color="warning"
              disabled={!!actionBusy}
              onClick={() => setQuestionOpen(true)}
            >
              Question
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={!!actionBusy}
              onClick={() => setDenyOpen(true)}
            >
              Deny
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={!!actionBusy || ticket?.status === 'cancelled'}
              startIcon={<CancelTicketIcon sx={{ fontSize: 16 }} />}
              onClick={() => { setCancelReason(''); setCancelResult(null); setCancelOpen(true); }}
            >
              Cancel Ticket
            </Button>
            <Button
              variant="outlined"
              disabled={!!actionBusy || activeTab !== 0}
              onClick={handleEdit}
              startIcon={actionBusy === 'edit' ? <CircularProgress size={16} /> : <EditIcon />}
            >
              {actionBusy === 'edit' ? 'Saving...' : 'Save Edits'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!!actionBusy || activeTab !== 0}
              onClick={handleEditAndApprove}
              startIcon={actionBusy ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {actionBusy === 'edit' || actionBusy === 'approve' ? 'Working...' : 'Edit & Approve'}
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={!!actionBusy}
              onClick={handleApprove}
              startIcon={actionBusy === 'approve' ? <CircularProgress size={16} /> : null}
            >
              {actionBusy === 'approve' ? 'Approving...' : 'Approve'}
            </Button>
          </>
        )}
        {/* Already-approved tickets (any status that is not pending_review/needs_info/denied/cancelled/completed) */}
        {fullTicket && !['awaiting_parts','pending_review','needs_info','denied','cancelled','completed'].includes(fullTicket.status) && (
          <>
            {['assigned','en_route','on_site','in_progress','pending_parts'].includes(fullTicket.status) && (
              <>
                <Button variant="outlined" color="warning" disabled={saving || !!actionBusy}
                  title="Job needs another visit — flags the ticket on the calendar & schedule"
                  onClick={async () => {
                    const notes = window.prompt('Return trip required. Notes for dispatch (optional):') || undefined;
                    try { await ticketsApi.visitOutcome(ticket.id, 'return_trip', notes); await refreshTicketAfter(); }
                    catch (err: any) { setActionError(err?.response?.data?.detail || 'Failed'); }
                  }}>
                  Return Trip Needed
                </Button>
                <Button variant="outlined" disabled={saving || !!actionBusy}
                  title="Unassigns the ticket and returns it to the dispatch queue"
                  onClick={async () => {
                    const notes = window.prompt('Send back to dispatch. Notes (optional):') || undefined;
                    try { await ticketsApi.visitOutcome(ticket.id, 'return_to_dispatch', notes); await refreshTicketAfter(); }
                    catch (err: any) { setActionError(err?.response?.data?.detail || 'Failed'); }
                  }}>
                  Back to Dispatch
                </Button>
                {/* change 16 (2026-11-01, P2-E): a tech may only Complete (close and bill) after this */}
                {['on_site','in_progress','pending_parts'].includes(fullTicket.status) && !(fullTicket as any)?.close_approved_by && (
                  <Button variant="contained" color="info" disabled={saving || !!actionBusy}
                    title="Lets the technician close and bill this call from the field (parts and time look right)"
                    startIcon={actionBusy === 'approve_close' ? <CircularProgress size={16} /> : null}
                    onClick={async () => {
                      const note = window.prompt('Approve close: the tech may now complete and bill this call. Note (optional):') ;
                      if (note === null) return;
                      setActionBusy('approve_close'); setActionError(null);
                      try { await ticketsApi.approveClose(ticket.id, { note: note || undefined }); await refreshTicketAfter(); }
                      catch (err: any) { setActionError(err?.response?.data?.detail || 'Approve close failed'); }
                      finally { setActionBusy(null); }
                    }}>
                    {actionBusy === 'approve_close' ? 'Approving...' : 'Approve close'}
                  </Button>
                )}
                {(fullTicket as any)?.close_approved_by && (
                  <Chip size="small" color="info" variant="outlined"
                    label={`Close approved by ${(fullTicket as any).close_approved_by}`}
                    title="Click to take the approval back"
                    onDelete={async () => {
                      if (!confirm('Take back the close approval? The tech will not be able to complete the call until it is approved again.')) return;
                      try { await ticketsApi.approveClose(ticket.id, { revoke: true }); await refreshTicketAfter(); }
                      catch (err: any) { setActionError(err?.response?.data?.detail || 'Revoke failed'); }
                    }} />
                )}
                <Button variant="contained" color="success" disabled={saving || !!actionBusy}
                  title="Complete the call — creates the draft invoice and deducts truck stock"
                  onClick={async () => {
                    if (!confirm('Complete this call? This finalizes time, creates the ERP draft invoice, and deducts parts from the truck.')) return;
                    try { await ticketsApi.changeStatus(ticket.id, 'completed'); await refreshTicketAfter(); }
                    catch (err: any) { setActionError(err?.response?.data?.detail || 'Complete failed'); }
                  }}>
                  Complete Call
                </Button>
              </>
            )}
            {!fullTicket?.return_ticket_id && (
              <Button
                variant="outlined"
                color="warning"
                disabled={saving || !!actionBusy}
                onClick={async () => {
                  if (!confirm('Close this ticket and create a NEW linked return call? Time/parts already logged stay billable on this ticket; the return visit becomes its own billable call linked to it.')) return;
                  setActionBusy('return');
                  try {
                    const r = await ticketsApi.closeCreateReturn(ticket.id, { reason: formData.return_reason || undefined, close_status: 'completed' });
                    onSaved();
                    onClose();
                    alert('Created linked return call ' + (r.data?.ticket_number || '') + '.');
                  } catch (err: any) {
                    setActionError(err?.response?.data?.detail || 'Close & create return failed');
                  } finally { setActionBusy(null); }
                }}
              >
                Close &amp; create return call
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              disabled={saving || !!actionBusy}
              onClick={async () => {
                if (!confirm('Cancel this ticket?')) return;
                try {
                  await ticketsApi.changeStatus(ticket.id, 'cancelled');
                  await refreshTicketAfter();
                } catch (err: any) {
                  setActionError(err?.response?.data?.detail || 'Cancel failed');
                }
              }}
            >
              Cancel Ticket
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || activeTab === 3 || !!actionBusy}
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              sx={{ background: 'linear-gradient(180deg, #1E4A6B, #0D2233)', '&:hover': { background: 'linear-gradient(180deg, #123A54, #0A1A28)' } }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        )}
        {/* Read-only states: needs_info / denied / cancelled / completed */}
        {fullTicket && fullTicket.status === 'completed' && (
          fullTicket.erpnext_invoice_no
            ? <Chip component="a" href={`https://erp.concept.bz/app/sales-invoice/${fullTicket.erpnext_invoice_no}`} target="_blank" clickable
                label={`Invoice ${fullTicket.erpnext_invoice_no} · ${fullTicket.erpnext_invoice_status === 'draft' ? 'DRAFT — review & submit in ERPNext' : (fullTicket.erpnext_invoice_status || 'created')}`}
                sx={{ fontWeight: 700, bgcolor: fullTicket.erpnext_invoice_status === 'draft' ? '#FFF4E5' : '#E6F4EA', color: fullTicket.erpnext_invoice_status === 'draft' ? '#B25E09' : '#1E8E3E' }} />
            : <Chip label="Invoice not created yet — the ERPNext worker picks completed calls up within a minute" sx={{ bgcolor: '#F1F3F5', color: '#6B7785' }} />
        )}
        {fullTicket && ['needs_info','denied','cancelled','completed'].includes(fullTicket.status) && (
          <Button variant="contained" disabled startIcon={null}>
            {fullTicket.status === 'needs_info' ? 'Awaiting submitter reply'
              : fullTicket.status === 'denied' ? 'Denied'
              : fullTicket.status === 'cancelled' ? 'Cancelled'
              : 'Completed'}
          </Button>
        )}
      </DialogActions>

      {/* Deny modal */}
      <Dialog open={denyOpen} onClose={() => setDenyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deny ticket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason for denial (sent to submitter)"
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyOpen(false)} disabled={actionBusy === 'deny'}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!denyReason.trim() || actionBusy === 'deny'}
            onClick={handleSubmitDeny}
            startIcon={actionBusy === 'deny' ? <CircularProgress size={16} /> : null}
          >
            {actionBusy === 'deny' ? 'Denying...' : 'Confirm Deny'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question modal */}
      <Dialog open={questionOpen} onClose={() => setQuestionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ask submitter for more info</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Question (sent to submitter, ticket moves to NEEDS_INFO)"
            value={questionBody}
            onChange={(e) => setQuestionBody(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionOpen(false)} disabled={actionBusy === 'question'}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!questionBody.trim() || actionBusy === 'question'}
            onClick={handleSubmitQuestion}
            startIcon={actionBusy === 'question' ? <CircularProgress size={16} /> : null}
          >
            {actionBusy === 'question' ? 'Sending...' : 'Send Question'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── D. CANCELLATION — who, why, when. Recorded in billing either way. ── */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel {ticket?.ticket_number}</DialogTitle>
        <DialogContent>
          {cancelResult ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Cancelled by <b>{cancelResult.cancelled_by}</b>. Recorded in billing.
              </Alert>
              <Typography variant="body2">Charges on this ticket: <b>${(cancelResult.charges?.total || 0).toFixed(2)}</b></Typography>
              {!!cancelResult.cancel_fee && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Late cancellation fee of <b>${Number(cancelResult.cancel_fee).toFixed(2)}</b> recorded — it goes on a DRAFT invoice for review before anything is issued.
                </Alert>
              )}
              {cancelResult.closed_no_charge && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No charges — the ticket has been fully closed out and sent to an admin for review.
                </Alert>
              )}
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                <InputLabel>Reason code</InputLabel>
                <Select value={cancelCode} label="Reason code" onChange={e => setCancelCode(e.target.value)}>
                  <MenuItem value="customer_request">Customer cancelled</MenuItem>
                  <MenuItem value="duplicate">Duplicate ticket</MenuItem>
                  <MenuItem value="created_in_error">Created in error</MenuItem>
                  <MenuItem value="no_access">No access to site</MenuItem>
                  <MenuItem value="resolved_remotely">Resolved remotely</MenuItem>
                  <MenuItem value="rescheduled">Rescheduled to a new call</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth multiline rows={3} required label="Why is this being cancelled?"
                value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                helperText="Recorded against the ticket and sent to billing with your name and the time." />
              {/* change 16 (2026-11-01, P2-E): customer-asked cancels inside the notice window record the late-cancellation fee */}
              <FormControlLabel sx={{ mt: 1 }}
                control={<Checkbox checked={cancelCustomerRequested} onChange={e => setCancelCustomerRequested(e.target.checked)} />}
                label="Customer asked to cancel (late-cancellation fee rule applies; leave unticked for Concept's own reasons)" />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setCancelOpen(false); if (cancelResult) { setCancelResult(null); onSaved(); } }}>
            {cancelResult ? 'Close' : 'Back'}
          </Button>
          {!cancelResult && (
            <Button variant="contained" color="error" disabled={!cancelReason.trim() || actionBusy === 'cancel'}
              startIcon={actionBusy === 'cancel' ? <CircularProgress size={16} /> : <CancelTicketIcon />}
              onClick={async () => {
                if (!ticket) return;
                setActionBusy('cancel');
                try {
                  const r = await ticketOpsApi.cancel(ticket.id, { reason: cancelReason.trim(), reason_code: cancelCode, customer_requested: cancelCustomerRequested });
                  setCancelResult(r.data);
                } catch (e: any) {
                  setError(e?.response?.data?.detail || 'Could not cancel the ticket.');
                  setCancelOpen(false);
                } finally { setActionBusy(null); }
              }}>
              Cancel ticket
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default function TicketsPage() {
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kanbanData, setKanbanData] = useState<KanbanBoard | null>(null);
  const [allTickets, setAllTickets] = useState<KanbanTicket[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<KanbanTicket | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deepLinkTab, setDeepLinkTab] = useState<number | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortMode, setSortMode] = useState<string>('smart');
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [ticketFilter, setTicketFilter] = useState<TicketFilterValue>(defaultTicketFilter());
  const [pageTechs, setPageTechs] = useState<any[]>([]);
  useEffect(() => { techniciansApi.list().then(r => setPageTechs(r.data || [])).catch(() => {}); }, []);
  const filterActive = ticketFilter.dateMode !== 'all' || ticketFilter.tech !== 'all' || ticketFilter.flags.missed || ticketFilter.flags.needs_return || ticketFilter.flags.aged;

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true); setError(null);
    try {
      const response = await ticketsApi.getKanbanBoard();
      setKanbanData(response.data);
      const all = [...response.data.unassigned, ...response.data.assigned, ...response.data.en_route, ...response.data.on_site, ...response.data.in_progress, ...response.data.pending_parts, ...response.data.completed_today];
      // Phase 7d (Agent D): kanban board endpoint only returns 7 fixed status columns
      // and ignores pending_review / needs_info / denied / cancelled. Pull those
      // separately so the list view + Status filter expose them properly.
      try {
        const extraStatuses = ['awaiting_parts', 'pending_review', 'needs_info', 'denied', 'cancelled', 'completed'];   // completed: latest 100 so Status=Completed / search find closed jobs (2026-09-04); awaiting_parts = install orders (2026-09-12)
        const lists = await Promise.all(extraStatuses.map(s =>
          ticketsApi.list({ status: s, page_size: 100 }).catch(() => ({ data: { items: [] } }))
        ));
        const haveIds = new Set(all.map((t: any) => t.id));
        lists.forEach((r: any) => {
          for (const t of (r.data.items || [])) {
            if (!haveIds.has(t.id)) { all.push(t); haveIds.add(t.id); }
          }
        });
      } catch (e) { /* non-fatal */ }
      setAllTickets(all);
    } catch (err) { console.error('Failed to fetch tickets:', err); setError('Failed to load tickets. Please try again.'); } finally { setLoading(false); }
  };

  
  // WebSocket push — instant refresh on any change
  useWSRefresh(['ticket:assigned', 'ticket:updated', 'ticket:status', 'ticket:created', 'ticket:deleted'], () => { fetchData(true); });

  useEffect(() => { fetchData(); const iv = setInterval(() => fetchData(true), 30000); return () => clearInterval(iv); }, []);

  // Server-side search across ALL tickets (not just the loaded board).
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) { setSearchResults(null); return; }
    setViewMode('list');
    const h = setTimeout(async () => {
      try { const r: any = await ticketsApi.list({ search: q, page_size: 200 }); setSearchResults(r.data.items || []); }
      catch { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(h);
  }, [searchQuery]);

  const handleTicketClick = (ticket: KanbanTicket) => { setSelectedTicket(ticket); setEditDialogOpen(true); };

  // Auto-open ticket from URL param ?id=
  useEffect(() => {
    const ticketId = searchParams.get('id');
    if (ticketId && allTickets.length > 0) {
      const found = allTickets.find(t => t.id === parseInt(ticketId));
      const openIt = (t: any) => {
        const tabName = searchParams.get('tab');
        setDeepLinkTab(tabName === 'files' ? 5 : tabName === 'billing' ? 4 : undefined);
        setSelectedTicket(t);
        setEditDialogOpen(true);
      };
      // not on the board (closed / beyond the first page)? fetch it directly so alert links always open (2026-09-04)
      if (!found) ticketsApi.get(parseInt(ticketId)).then((r: any) => { if (r.data) openIt(r.data); }).catch(() => {});
      {
        if (found) openIt(found);
        // Clear the param so refresh doesn't re-open
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('id'); newParams.delete('tab');
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [allTickets, searchParams, setSearchParams]);

  const daysSince = (d?: string) => d ? (Date.now() - new Date(d).getTime()) / 86400000 : -1;
  const sortTickets = (arr: any[]) => {
    const grp = (t: any) => {
      const st = t.status;
      const active = ['assigned','scheduled','en_route','on_site','in_progress','pending_parts'].includes(st);
      if (active && daysSince(t.assigned_at || t.scheduled_start) > 15) return 0; // overdue assigned (>15d since approval)
      if (st === 'pending_review' || st === 'needs_info') return 1;               // unapproved
      return 2;                                                                   // everything else
    };
    const c = [...arr];
    if (sortMode === 'newest') return c.sort((a,b)=> new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    if (sortMode === 'oldest') return c.sort((a,b)=> new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    if (sortMode === 'status') return c.sort((a,b)=> String(a.status).localeCompare(String(b.status)));
    return c.sort((a,b) => {
      const ga = grp(a), gb = grp(b);
      if (ga !== gb) return ga - gb;
      if (ga === 2) return new Date(b.created_at).getTime() - new Date(a.created_at).getTime(); // approved: newest first
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();               // overdue/unapproved: oldest first
    });
  };
  const sourceTickets: any[] = searchResults !== null ? searchResults : allTickets;
  const filteredTickets = sortTickets(sourceTickets.filter((ticket: any) => {
    if (filterPriority !== 'all' && ticket.priority !== filterPriority) return false;
    if (filterStatus !== 'all' && ticket.status !== filterStatus) return false;
    if (!ticketMatchesFilter(ticket, ticketFilter)) return false;
    return true;
  }));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 2, sm: 0 }, mb: { xs: 2, md: 3 } }}>
        <Box><Typography variant="h4" sx={{ fontWeight: 700, color: '#0D2233', mb: 0.5 }}>Tickets</Typography><Typography variant="body2" color="text.secondary">Manage and track all service tickets</Typography></Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}><Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>New Ticket</Button><IconButton onClick={() => fetchData()} disabled={loading} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}><RefreshIcon /></IconButton></Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField placeholder="Search by ticket #, title, customer, or location..." size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ minWidth: 250 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment> }} />
        <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Priority</InputLabel><Select value={filterPriority} label="Priority" onChange={(e) => setFilterPriority(e.target.value)}><MenuItem value="all">All</MenuItem><MenuItem value="urgent">Urgent</MenuItem><MenuItem value="high">High</MenuItem><MenuItem value="medium">Medium</MenuItem><MenuItem value="low">Low</MenuItem></Select></FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}><InputLabel>Status</InputLabel><Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}><MenuItem value="all">All</MenuItem><MenuItem value="awaiting_parts">Awaiting Parts (install)</MenuItem><MenuItem value="pending_review">Pending Review</MenuItem><MenuItem value="needs_info">Needs Info</MenuItem><MenuItem value="new">New</MenuItem><MenuItem value="unassigned">Unassigned</MenuItem><MenuItem value="assigned">Assigned</MenuItem><MenuItem value="en_route">En Route</MenuItem><MenuItem value="on_site">On Site</MenuItem><MenuItem value="in_progress">In Progress</MenuItem><MenuItem value="pending_parts">Pending Parts</MenuItem><MenuItem value="completed">Completed</MenuItem><MenuItem value="denied">Denied</MenuItem><MenuItem value="cancelled">Cancelled</MenuItem></Select></FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Sort</InputLabel><Select value={sortMode} label="Sort" onChange={(e) => setSortMode(e.target.value)}><MenuItem value="smart">Smart (default)</MenuItem><MenuItem value="newest">Newest first</MenuItem><MenuItem value="oldest">Oldest first</MenuItem><MenuItem value="status">By status</MenuItem></Select></FormControl>
        <TicketFilters value={ticketFilter} onChange={(v) => { setTicketFilter(v); const anyOn = v.dateMode !== 'all' || v.tech !== 'all' || v.flags.missed || v.flags.needs_return || v.flags.aged; if (anyOn) setViewMode('list'); }} technicians={pageTechs} />
        <Box sx={{ flex: 1 }} />
        <Tabs value={viewMode} onChange={(_, v) => setViewMode(v)} sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0 } }}><Tab icon={<KanbanIcon />} value="kanban" sx={{ minWidth: 50 }} /><Tab icon={<ListIcon />} value="list" sx={{ minWidth: 50 }} /></Tabs>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {viewMode === 'kanban' ? (
          <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, height: '100%' }}>
            <KanbanColumn title="Awaiting Parts (install)" tickets={allTickets.filter((t:any)=>t.status==='awaiting_parts')} color={INSTALL_COLOR} onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="Pending Review" tickets={allTickets.filter((t:any)=>t.status==='pending_review')} color="#f59e0b" onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="Needs Info" tickets={allTickets.filter((t:any)=>t.status==='needs_info')} color="#a855f7" onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="Unassigned" tickets={kanbanData?.unassigned || []} color="#6366f1" onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="Assigned" tickets={kanbanData?.assigned || []} color="#3b82f6" onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="En Route" tickets={kanbanData?.en_route || []} color="#f59e0b" onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="On Site" tickets={kanbanData?.on_site || []} color="#10b981" onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="In Progress" tickets={kanbanData?.in_progress || []} color="#06b6d4" onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="Pending Parts" tickets={kanbanData?.pending_parts || []} color="#ec4899" onTicketClick={handleTicketClick} loading={loading} />
            <KanbanColumn title="Completed Today" tickets={kanbanData?.completed_today || []} color="#22c55e" onTicketClick={handleTicketClick} loading={loading} />
          </Box>
        ) : (
          <Card sx={{ height: '100%', overflow: 'auto' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: '0 0 140px' }}><Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>TICKET #</Typography></Box>
                <Box sx={{ flex: 1 }}><Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>TITLE</Typography></Box>
                <Box sx={{ flex: '0 0 100px' }}><Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>PRIORITY</Typography></Box>
                <Box sx={{ flex: '0 0 120px' }}><Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>STATUS</Typography></Box>
                <Box sx={{ flex: '0 0 100px' }}><Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>ASSIGNED</Typography></Box>
                <Box sx={{ flex: '0 0 100px' }}><Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>CREATED</Typography></Box>
              </Box>
            </Box>
            {loading ? [...Array(5)].map((_, i) => <Skeleton key={i} variant="rectangular" height={60} sx={{ m: 1 }} />) : filteredTickets.length > 0 ? filteredTickets.map((ticket) => <TicketRow key={ticket.id} ticket={ticket} onClick={() => handleTicketClick(ticket)} />) : <Box sx={{ textAlign: 'center', py: 8 }}><TicketIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} /><Typography color="text.secondary">No tickets found</Typography></Box>}
          </Card>
        )}
      </Box>
      <CreateTicketDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} onCreated={fetchData} />
      <EditTicketDialog ticket={selectedTicket} open={editDialogOpen} initialTab={deepLinkTab} onClose={() => { setEditDialogOpen(false); setSelectedTicket(null); setDeepLinkTab(undefined); }} onSaved={fetchData} />
    </Box>
  );
}
